import { LinkedInWizardData } from "@/pages/LinkedInWizard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  User, 
  Target, 
  Star, 
  Users, 
  Building2, 
  FileText, 
  Trophy, 
  MessageSquare, 
  Hash, 
  Briefcase,
  Heart,
  PhoneCall,
  CheckCircle2,
  Edit3,
  Sparkles
} from "lucide-react";

interface LinkedInWizardRecapProps {
  data: LinkedInWizardData;
  onEdit: (step: number) => void;
  onGenerate: () => void;
  onBack: () => void;
}

export function LinkedInWizardRecap({ data, onEdit, onGenerate, onBack }: LinkedInWizardRecapProps) {
  const getCompletionStats = () => {
    let completed = 0;
    let total = 11;
    
    if (data.userCategories?.length > 0) completed++;
    if (data.primaryGoals?.length > 0) completed++;
    if (data.uniqueSellingPoints?.length > 0) completed++;
    if (data.targetAudience && data.whatYouHelp && data.howYouHelp && data.valueProposition) completed++; // Include headline requirement
    if (data.industries?.length > 0) completed++;
    if (data.uploadedFiles?.length > 0 || data.pastedText || data.manuallyEnteredText) completed++;
    if (data.skipExperienceHighlights || data.experienceHighlights?.length > 0) completed++;
    if (data.skipResultsAndProof || data.resultsAndProof) completed++;
    if (data.voiceStyle) completed++;
    if (data.keywords || data.targetJobTitles || data.skills) completed++;
    if (data.callToAction && data.callToAction !== 'not-sure') completed++; // Exclude 'not-sure' option
    
    return { completed, total, percentage: Math.round((completed / total) * 100) };
  };

  const stats = getCompletionStats();
  
  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-3xl font-bold mb-2">📋 Review Your Profile Data</h2>
        <p className="text-muted-foreground mb-4">
          Please review all the information we've collected. You can edit any section before generating your LinkedIn profile.
        </p>
        
        {/* Completion Status */}
        <Card className="p-4 bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-950 dark:to-blue-950 border-green-200 dark:border-green-800">
          <div className="flex items-center justify-center gap-4">
            <CheckCircle2 className="w-6 h-6 text-green-600" />
            <div>
              <span className="text-lg font-semibold text-green-800 dark:text-green-200">
                {stats.completed}/{stats.total} Sections Complete
              </span>
              <p className="text-sm text-green-600 dark:text-green-400">
                {stats.percentage}% of your profile information collected
              </p>
            </div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Step 1: User Categories */}
        <Card className="p-6 hover:shadow-md transition-shadow">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <User className="w-5 h-5 text-blue-600" />
              <h3 className="font-semibold">Your Role</h3>
            </div>
            <Button variant="ghost" size="sm" onClick={() => onEdit(1)}>
              <Edit3 className="w-4 h-4" />
            </Button>
          </div>
          {data.userCategories?.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {data.userCategories.map((category, index) => (
                <Badge key={index} variant="secondary">{category}</Badge>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-sm">No categories selected</p>
          )}
        </Card>

        {/* Step 2: Primary Goals */}
        <Card className="p-6 hover:shadow-md transition-shadow">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <Target className="w-5 h-5 text-green-600" />
              <h3 className="font-semibold">Primary Goals</h3>
            </div>
            <Button variant="ghost" size="sm" onClick={() => onEdit(2)}>
              <Edit3 className="w-4 h-4" />
            </Button>
          </div>
          {data.primaryGoals?.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {data.primaryGoals.map((goal, index) => (
                <Badge key={index} variant="secondary">{goal}</Badge>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-sm">No goals selected</p>
          )}
        </Card>

        {/* Step 3: Unique Selling Points */}
        <Card className="p-6 hover:shadow-md transition-shadow">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <Star className="w-5 h-5 text-yellow-600" />
              <h3 className="font-semibold">Unique Selling Points</h3>
            </div>
            <Button variant="ghost" size="sm" onClick={() => onEdit(3)}>
              <Edit3 className="w-4 h-4" />
            </Button>
          </div>
          {data.uniqueSellingPoints?.length > 0 ? (
            <div className="space-y-2">
              {data.uniqueSellingPoints.map((point, index) => (
                <Badge key={index} variant="secondary" className="mr-2">{point}</Badge>
              ))}
              {data.customUSP && (
                <p className="text-sm mt-2"><strong>Custom:</strong> {data.customUSP}</p>
              )}
            </div>
          ) : (
            <p className="text-muted-foreground text-sm">No selling points selected</p>
          )}
        </Card>

        {/* Step 4: Value Proposition & Selected Headline */}
        <Card className="p-6 hover:shadow-md transition-shadow">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <MessageSquare className="w-5 h-5 text-purple-600" />
              <h3 className="font-semibold">Value Proposition</h3>
            </div>
            <Button variant="ghost" size="sm" onClick={() => onEdit(4)}>
              <Edit3 className="w-4 h-4" />
            </Button>
          </div>
          <div className="space-y-3 text-sm">
            <p><strong>Who you help:</strong> {data.whatYouHelp || 'Not specified'}</p>
            <p><strong>Target audience:</strong> {data.targetAudience || 'Not specified'}</p>
            <p><strong>How you help:</strong> {data.howYouHelp || 'Not specified'}</p>
            
            {/* Selected LinkedIn Headline */}
            {data.valueProposition && (
              <div className="mt-4 p-3 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-950 dark:to-purple-950 rounded-lg border border-blue-200 dark:border-blue-800">
                <p className="text-xs font-medium text-blue-800 dark:text-blue-200 mb-1">🎯 Selected LinkedIn Headline:</p>
                <p className="font-medium text-blue-900 dark:text-blue-100 text-sm leading-relaxed">
                  "{data.valueProposition}"
                </p>
                {data.generatedHeadlines && (
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                    Selected from {data.generatedHeadlines.length} generated options
                  </p>
                )}
              </div>
            )}
            
            {!data.valueProposition && (
              <div className="mt-4 p-3 bg-orange-50 dark:bg-orange-950 rounded-lg border border-orange-200 dark:border-orange-800">
                <p className="text-sm text-orange-800 dark:text-orange-200">
                  ⚠️ No LinkedIn headline selected yet
                </p>
              </div>
            )}
          </div>
        </Card>

        {/* Step 5: Industries & Audience */}
        <Card className="p-6 hover:shadow-md transition-shadow">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <Building2 className="w-5 h-5 text-indigo-600" />
              <h3 className="font-semibold">Industries & Audience</h3>
            </div>
            <Button variant="ghost" size="sm" onClick={() => onEdit(5)}>
              <Edit3 className="w-4 h-4" />
            </Button>
          </div>
          <div className="space-y-3">
            {data.industries?.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-1">Industries:</p>
                <div className="flex flex-wrap gap-1">
                  {data.industries.map((industry, index) => (
                    <Badge key={index} variant="outline" className="text-xs">{industry}</Badge>
                  ))}
                </div>
              </div>
            )}
            <div className="text-sm">
              <p><strong>Seniority:</strong> {data.audienceSeniority || 'Not specified'}</p>
              <p><strong>Company Size:</strong> {data.companySize || 'Not specified'}</p>
            </div>
          </div>
        </Card>

        {/* Step 6: Documents */}
        <Card className="p-6 hover:shadow-md transition-shadow">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <FileText className="w-5 h-5 text-orange-600" />
              <h3 className="font-semibold">Documents & Information</h3>
            </div>
            <Button variant="ghost" size="sm" onClick={() => onEdit(6)}>
              <Edit3 className="w-4 h-4" />
            </Button>
          </div>
          <div className="space-y-2 text-sm">
            {data.uploadedFiles?.length > 0 && (
              <p>📎 {data.uploadedFiles.length} file(s) uploaded</p>
            )}
            {data.pastedText && (
              <p>📝 Text content pasted</p>
            )}
            {data.manuallyEnteredText && (
              <p>✏️ Manual information entered</p>
            )}
            {!data.uploadedFiles?.length && !data.pastedText && !data.manuallyEnteredText && (
              <p className="text-muted-foreground">No documents or information provided</p>
            )}
          </div>
        </Card>

        {/* Step 7: Experience Highlights */}
        <Card className="p-6 hover:shadow-md transition-shadow">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <Trophy className="w-5 h-5 text-amber-600" />
              <h3 className="font-semibold">Experience Highlights</h3>
            </div>
            <Button variant="ghost" size="sm" onClick={() => onEdit(7)}>
              <Edit3 className="w-4 h-4" />
            </Button>
          </div>
          {data.skipExperienceHighlights ? (
            <p className="text-sm text-muted-foreground">Skipped - using uploaded documents</p>
          ) : data.experienceHighlights?.length > 0 ? (
            <div className="space-y-2">
              {data.experienceHighlights.map((exp, index) => (
                <div key={index} className="text-sm border-l-2 border-amber-200 pl-3">
                  <p className="font-medium">{exp.role}</p>
                  <p className="text-muted-foreground">{exp.achievements}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-sm">No experience highlights provided</p>
          )}
        </Card>

        {/* Step 8: Results & Proof */}
        <Card className="p-6 hover:shadow-md transition-shadow">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              <h3 className="font-semibold">Results & Proof</h3>
            </div>
            <Button variant="ghost" size="sm" onClick={() => onEdit(8)}>
              <Edit3 className="w-4 h-4" />
            </Button>
          </div>
          {data.skipResultsAndProof ? (
            <p className="text-sm text-muted-foreground">Skipped - using uploaded documents</p>
          ) : data.resultsAndProof ? (
            <p className="text-sm">{data.resultsAndProof}</p>
          ) : (
            <p className="text-muted-foreground text-sm">No results provided</p>
          )}
        </Card>

        {/* Step 9: Voice & Style */}
        <Card className="p-6 hover:shadow-md transition-shadow">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <MessageSquare className="w-5 h-5 text-pink-600" />
              <h3 className="font-semibold">Voice & Style</h3>
            </div>
            <Button variant="ghost" size="sm" onClick={() => onEdit(9)}>
              <Edit3 className="w-4 h-4" />
            </Button>
          </div>
          <div className="space-y-2 text-sm">
            <p><strong>Voice Style:</strong> {data.voiceStyle || 'Not selected'}</p>
            <div className="flex items-center gap-2">
              <Heart className="w-4 h-4 text-red-500" />
              <span><strong>Include Faith:</strong> {data.includeFaith || 'Not specified'}</span>
            </div>
          </div>
        </Card>

        {/* Step 10: Keywords & Skills */}
        <Card className="p-6 hover:shadow-md transition-shadow">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <Hash className="w-5 h-5 text-cyan-600" />
              <h3 className="font-semibold">Keywords & Skills</h3>
            </div>
            <Button variant="ghost" size="sm" onClick={() => onEdit(10)}>
              <Edit3 className="w-4 h-4" />
            </Button>
          </div>
          <div className="space-y-3 text-sm">
            {data.keywords && (
              <div>
                <p className="font-medium">Keywords:</p>
                <p>{data.keywords}</p>
              </div>
            )}
            {data.targetJobTitles && (
              <div>
                <p className="font-medium">Target Job Titles:</p>
                <p>{data.targetJobTitles}</p>
              </div>
            )}
            {data.skills && (
              <div>
                <p className="font-medium">Skills:</p>
                <p>{data.skills}</p>
              </div>
            )}
            {data.pastJobTitles && (
              <div>
                <p className="font-medium">Past Job Titles:</p>
                <p>{data.pastJobTitles}</p>
              </div>
            )}
          </div>
        </Card>

        {/* Step 11: Call to Action */}
        <Card className="p-6 hover:shadow-md transition-shadow">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <PhoneCall className="w-5 h-5 text-red-600" />
              <h3 className="font-semibold">Call to Action</h3>
            </div>
            <Button variant="ghost" size="sm" onClick={() => onEdit(11)}>
              <Edit3 className="w-4 h-4" />
            </Button>
          </div>
          {data.callToAction ? (
            <Badge variant="secondary">{data.callToAction.replace('-', ' ')}</Badge>
          ) : (
            <p className="text-muted-foreground text-sm">No call to action selected</p>
          )}
        </Card>
      </div>

      {/* Action Buttons */}
      <Card className="p-6 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-950 dark:to-purple-950 border-blue-200 dark:border-blue-800">
        <div className="text-center space-y-4">
          <div className="flex items-center justify-center gap-3">
            <Sparkles className="w-6 h-6 text-blue-600" />
            <h3 className="text-xl font-semibold text-blue-800 dark:text-blue-200">
              Ready to Generate Your LinkedIn Profile?
            </h3>
          </div>
          
          <p className="text-blue-700 dark:text-blue-300 max-w-2xl mx-auto">
            Your profile will be crafted using AI to create compelling headlines, engaging summaries, 
            and optimized content that showcases your unique value proposition and attracts your target audience.
          </p>
          
          <div className="flex gap-4 justify-center pt-4">
            <Button variant="outline" onClick={onBack}>
              Back to Edit
            </Button>
            <Button 
              onClick={onGenerate}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8"
              disabled={stats.completed < 8 || !data.valueProposition || !data.callToAction || data.callToAction === 'not-sure'} // Require headline and valid CTA
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Generate My LinkedIn Profile
            </Button>
          </div>
          
          {(stats.completed < 8 || !data.valueProposition || !data.callToAction || data.callToAction === 'not-sure') && (
            <div className="space-y-1 mt-2">
              {stats.completed < 8 && (
                <p className="text-sm text-orange-600 dark:text-orange-400">
                  Please complete at least 8 sections before generating your profile.
                </p>
              )}
              {!data.valueProposition && (
                <p className="text-sm text-orange-600 dark:text-orange-400">
                  Please select a LinkedIn headline in Step 4.
                </p>
              )}
              {(!data.callToAction || data.callToAction === 'not-sure') && (
                <p className="text-sm text-orange-600 dark:text-orange-400">
                  Please select a call to action in Step 11.
                </p>
              )}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}